import type { AppState, RedirectLoginOptions } from "@auth0/auth0-react";
import { currentReturnToPath } from "@/lib/auth/authRedirectNavigate";
import { logger } from "@/lib/logger";

/**
 * The subset of the Auth0 SDK we need to recover — derived from the real
 * `useAuth0().loginWithRedirect` signature so it can't drift from the SDK.
 */
export type LoginWithRedirect = (
  options?: RedirectLoginOptions<AppState>
) => Promise<void>;

/**
 * Auth0 `error` codes that mean silent token acquisition cannot succeed without
 * sending the user through an interactive login. Retrying silently on these
 * only produces the "External interaction required" flood (LAR-290): the
 * refresh-token grant has failed (e.g. rotation reuse) and the `prompt=none`
 * iframe fallback cannot recover — see
 * docs/investigations/auth0-external-interaction-required-2026-07-13.md.
 */
const INTERACTIVE_AUTH_ERROR_CODES = new Set([
  "login_required",
  "interaction_required",
  "consent_required",
  "missing_refresh_token",
  // The Auth0 post-login action denies a login it cannot resolve an organization for, and silent
  // auth surfaces that as access_denied (LAR-437). Retrying silently only repeats the denial; the
  // interactive login is what puts the reason in front of the user, on the sign-in screen.
  "access_denied",
]);

/** The Auth0 `error` code from a thrown SDK error, if it carries one. */
export const getAuth0ErrorCode = (error: unknown): string | undefined => {
  const code = (error as { error?: unknown } | null | undefined)?.error;
  return typeof code === "string" ? code : undefined;
};

/** True when the Auth0 error requires an interactive re-login to recover. */
export const isInteractiveAuthError = (error: unknown): boolean => {
  const code = getAuth0ErrorCode(error);
  return code !== undefined && INTERACTIVE_AUTH_ERROR_CODES.has(code);
};

/**
 * Circuit-breaker: once an interactive re-login has been triggered we must not
 * trigger it again, otherwise every concurrent token consumer (the refresh
 * timer + every in-flight API request) races to redirect. The breaker is
 * module-level because a single browser session has a single Auth0 session to
 * recover, and the redirect navigates the whole page away anyway.
 */
let recoveryInProgress = false;

/**
 * Latched while a sign-out is under way. `handleLogout` starts Auth0's logout redirect and then
 * clears the SDK cache, so until the browser leaves the page the app still looks authenticated with
 * no token to serve: an in-flight request fails silent auth with `interaction_required`, and
 * recovering from that answered the user's logout with `loginWithRedirect`, which won the navigation
 * race and dropped them on the branded sign-in screen instead of signing them out (LAR-437).
 *
 * A completed logout navigates the page away, which clears this by itself.
 */
let signingOut = false;

/** Called before Auth0's logout redirect so nothing re-enters a login on the way out. */
export const beginSignOut = (): void => {
  signingOut = true;
};

/** Re-arm after a sign-out that never navigated, so the app is not left unable to recover. */
export const endSignOut = (): void => {
  signingOut = false;
};

/**
 * True while no token request should reach silent auth: an interactive re-login is already under
 * way, or the user is signing out.
 */
export const isRecoveringAuth = (): boolean => recoveryInProgress || signingOut;

/** Re-arm the breaker (call after a successful interactive login completes). */
export const resetAuthRecovery = (): void => {
  recoveryInProgress = false;
};

/**
 * If `error` requires interactive re-login, trigger `loginWithRedirect` once and
 * return true (so callers stop retrying silently). Returns false for transient
 * errors, which callers may retry with bounded backoff.
 */
export const recoverFromInteractiveAuthError = (
  error: unknown,
  loginWithRedirect: LoginWithRedirect
): boolean => {
  if (!isInteractiveAuthError(error)) return false;

  return triggerInteractiveLogin(
    loginWithRedirect,
    "Silent token refresh needs interaction; redirecting to login",
    "recoverFromInteractiveAuthError",
    getAuth0ErrorCode(error)
  );
};

/**
 * Recover from holding an access token with no organization claim. Silent auth "succeeded", so
 * there is no Auth0 error to inspect — but the token cannot be used: the API rejects it on every
 * tenant-scoped endpoint (LAR-437). Only an interactive login can produce an org-scoped token, or
 * fail with a reason the sign-in screen shows.
 *
 * Shares the breaker with {@link recoverFromInteractiveAuthError}: one browser session has one
 * Auth0 session to recover, whichever reason got there first.
 */
export const recoverFromMissingOrganization = (
  loginWithRedirect: LoginWithRedirect
): boolean =>
  triggerInteractiveLogin(
    loginWithRedirect,
    "Access token carries no organization; redirecting to login",
    "recoverFromMissingOrganization"
  );

/** Trip the breaker and redirect to an interactive login, once. */
const triggerInteractiveLogin = (
  loginWithRedirect: LoginWithRedirect,
  message: string,
  operation: string,
  auth0Error?: string
): boolean => {
  // Signing out outranks every reason to log back in.
  if (signingOut || recoveryInProgress) return true;

  recoveryInProgress = true;

  logger.warn(message, { operation, auth0Error });

  const returnTo = currentReturnToPath();
  // If the redirect itself fails, re-arm the breaker so we don't wedge the app
  // in a permanent fast-reject state (worse than the flood it replaces).
  loginWithRedirect({ appState: { returnTo } }).catch((redirectError) => {
    resetAuthRecovery();
    logger.error("Interactive login redirect failed", {
      operation,
      error: redirectError,
      auth0Error,
    });
  });

  return true;
};
